import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  PendingStaffInviteListResponse,
  ResendStaffInviteResponse,
  RevokeStaffInviteResponse,
  StaffInviteAISuggestRequest,
  StaffInviteAISuggestResponse,
  StaffInvitePreviewResponse,
  StaffInviteRowInput,
  StaffInviteSendResponse,
} from "@eduviaos/shared";
import { api } from "@/lib/api";

/**
 * Phase C1b — IA staff-invite hooks.
 *
 * The wizard's invite-staff step calls these in sequence:
 *
 *  1. ``usePreviewStaffInvites`` — POST `/me/first-run/staff-invites/preview`
 *     for live row-level verdicts as the principal edits the table.
 *  2. ``useSendStaffInvites`` — POST `/me/first-run/staff-invites` once
 *     they're happy with the verdicts. Server returns sent + skipped split.
 *  3. ``usePendingStaffInvites`` — GET `/me/first-run/staff-invites` to
 *     render the "already invited" list across renders / refreshes.
 *  4. ``useRevokeStaffInvite`` — POST `/.../revoke` so the principal can
 *     undo a fat-finger before the recipient redeems.
 *
 * All four target the same tenant scoped by the access token; no
 * tenant_id needs to ride in the URL.
 */

export function usePendingStaffInvites() {
  return useQuery<PendingStaffInviteListResponse>({
    queryKey: ["staff-invites", "pending"],
    queryFn: () => api.get<PendingStaffInviteListResponse>("/me/first-run/staff-invites"),
    staleTime: 30 * 1000,
  });
}

export function usePreviewStaffInvites() {
  return useMutation<StaffInvitePreviewResponse, Error, StaffInviteRowInput[]>({
    mutationKey: ["staff-invites", "preview"],
    mutationFn: (rows) =>
      api.post<StaffInvitePreviewResponse>("/me/first-run/staff-invites/preview", {
        rows,
      }),
  });
}

export function useSendStaffInvites() {
  const qc = useQueryClient();
  return useMutation<StaffInviteSendResponse, Error, StaffInviteRowInput[]>({
    mutationKey: ["staff-invites", "send"],
    mutationFn: (rows) =>
      api.post<StaffInviteSendResponse>("/me/first-run/staff-invites", { rows }),
    onSuccess: () => {
      // Force the pending-list to re-fetch so the wizard's "already
      // invited" panel reflects the new rows; we don't optimistically
      // patch because the BE adds derived metadata (email send audit,
      // expires_at) that the FE shouldn't try to recompute.
      void qc.invalidateQueries({ queryKey: ["staff-invites", "pending"] });
    },
  });
}

export function useRevokeStaffInvite() {
  const qc = useQueryClient();
  return useMutation<RevokeStaffInviteResponse, Error, string>({
    mutationKey: ["staff-invites", "revoke"],
    mutationFn: (inviteId) =>
      api.post<RevokeStaffInviteResponse>(`/me/first-run/staff-invites/${inviteId}/revoke`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["staff-invites", "pending"] });
      // Phase E1d — the dashboard summary aggregates `pending_staff_invites`
      // separately; bust that cache too so the dashboard card reflects the
      // revocation immediately.
      void qc.invalidateQueries({ queryKey: ["dashboard", "summary"] });
    },
  });
}

/**
 * Phase E1d — resend a PENDING staff invite from the dashboard. Uses the
 * BE endpoint shipped in E1a (`POST /.../staff-invites/{id}/resend`)
 * which rotates the underlying token, extends the TTL, and re-sends the
 * invite email. The previous accept URL is dead the moment this resolves.
 *
 * Errors:
 *   - 404 NOT_FOUND if the invite was deleted under us (race with revoke
 *     on another tab) — surface a generic "not found" toast.
 *   - 409 INVITE_NOT_PENDING if the invite was already accepted/revoked —
 *     same toast as above; refetch the list will remove the stale row.
 */
export function useResendStaffInvite() {
  const qc = useQueryClient();
  return useMutation<ResendStaffInviteResponse, Error, string>({
    mutationKey: ["staff-invites", "resend"],
    mutationFn: (inviteId) =>
      api.post<ResendStaffInviteResponse>(`/me/first-run/staff-invites/${inviteId}/resend`),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["staff-invites", "pending"] });
      // Bump dashboard so the `email_send_count` / `last_email_sent_at`
      // columns refresh visually.
      void qc.invalidateQueries({ queryKey: ["dashboard", "summary"] });
    },
  });
}

/**
 * Phase H3 — AI-suggested permissions for a staff invite.
 *
 * Wraps `POST /me/first-run/staff-invites/ai-suggest`. Deliberately a
 * `useMutation` rather than `useQuery` — we only fire on explicit user
 * intent ("Suggest permissions" button click), so the AI bill stays
 * bounded and the wizard doesn't fan out one call per row on mount.
 *
 * The BE re-uses the F-domain Gemini→NVIDIA chain with the staff-only
 * ceiling injected as the AI's allow-list. Every suggestion in the
 * response is already filtered against (a) the canonical permission
 * catalog and (b) the staff ceiling (non-blocklisted), so the drawer
 * can render the checkbox list without further client-side filtering.
 *
 * Errors:
 *   - 400 PAYLOAD_INVALID  → label / role missing or malformed.
 *   - 403 FORBIDDEN        → caller lacks the first-run guard role.
 *   - 422 VALIDATION_ERROR → role outside the allow-list.
 *   - 503 AI_UNAVAILABLE   → both providers degraded; surface the
 *     "AI temporarily unavailable" hint and let the admin proceed
 *     manually (Iron Rule #2 graceful-degradation).
 */
export function useStaffInviteAISuggest() {
  return useMutation<StaffInviteAISuggestResponse, Error, StaffInviteAISuggestRequest>({
    mutationKey: ["staff-invites", "ai-suggest"],
    mutationFn: (body) =>
      api.post<StaffInviteAISuggestResponse>("/me/first-run/staff-invites/ai-suggest", body),
  });
}
